import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import FmdGoodIcon from '@mui/icons-material/FmdGood';
import WrongLocationIcon from '@mui/icons-material/WrongLocation';
import { Autocomplete, Box, InputAdornment, TextField } from '@mui/material';
import { debounce } from 'lodash/function';
import { isEmpty } from 'lodash/lang';

import { MapsApi } from '../../../../../../../../api';
import { onRequestError } from '../../../../../../../../services/handlers';
import { NSC_ADDRESS } from '../../../../../../../../services/i18n/namespaces';

export default function Address1Autocomplete({
  name,
  matchedCountry,
  matchedRegion,
  city,
  address1,
  valid,
  onChange,
  onBlur,
  error,
  helperText,
  readOnly = false
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState([]);
  const prevRef = useRef(open);

  const value = useMemo(
    function() {
      if (!address1) {
        return { address: '', description: '' };
      }
      if (!isEmpty(options)) {
        const f = options.find(function(option) {
          return address1 === option.address;
        });
        if (!isEmpty(f)) return f;
      }
      return { address: address1, description: '' };
    },
    [address1, options]
  );

  const getPredictions = useMemo(function() {
    return debounce(function(req, resolve, reject, finish) {
      MapsApi.autocompleteAddress(req)
        .then(resolve, reject)
        .finally(finish);
    }, 1000);
  }, []);

  useEffect(
    function() {
      if (!open) {
        return;
      }
      if (address1?.length < 2 || !matchedCountry || !matchedRegion || !city) {
        setLoading(false);
        setOptions([]);
        return;
      }
      const request = prepareRequest(address1, city, matchedCountry, matchedRegion);
      const uid = Object.values(request).join('.');
      if (uid === prevRef.current) {
        return;
      }
      prevRef.current = uid;
      let canceled = false;
      setLoading(true);
      getPredictions(
        request,
        function({ data }) {
          if (!canceled) {
            setOptions(data.predictions);
          }
        },
        onRequestError,
        function() {
          if (!canceled) {
            setLoading(false);
          }
        }
      );
      return function() {
        canceled = true;
      };
    },
    [city, matchedCountry, matchedRegion, getPredictions, address1, open]
  );

  return (
    <Autocomplete
      disabled={readOnly}
      freeSolo
      loading={loading}
      filterOptions={x => x}
      noOptionsText={address1?.length < 2 ? 'Type at least 2 characters' : 'No options'}
      value={value}
      open={open}
      onOpen={() => {
        setOpen(true);
      }}
      onClose={() => {
        setOpen(false);
      }}
      onBlur={onBlur}
      fullWidth
      getOptionKey={option => option.description}
      getOptionLabel={option => option.address}
      renderOption={function(props, option) {
        return (
          <Box component="li" {...props}>
            {option.description}
          </Box>
        );
      }}
      disablePortal
      options={loading ? [] : options}
      onInputChange={(event, newInputValue) => {
        if (address1 !== newInputValue) {
          onChange(newInputValue);
        }
      }}
      renderInput={params => (
        <TextField
          {...params}
          name={name}
          label={t(`${NSC_ADDRESS}:address1`)}
          variant="standard"
          required
          error={error}
          helperText={helperText}
          autoComplete="off"
          slotProps={{
            input: {
              ...params.InputProps,
              startAdornment: (
                <InputAdornment position="start">
                  {valid ? <FmdGoodIcon color="primary" fontSize="small" /> : <WrongLocationIcon fontSize="small" />}
                </InputAdornment>
              )
            }
          }}
        />
      )}
    />
  );
}

function prepareRequest(address, city, matchedCountry, matchedRegion) {
  const obj = { address, city };
  if (matchedCountry?.name) {
    obj.country = matchedCountry?.name || null;
  }
  if (obj.country && (matchedRegion?.localName || matchedRegion?.name)) {
    obj.region = matchedRegion?.localName || matchedRegion?.name || null;
  }
  return obj;
}
